import React, {PureComponent} from 'react';
import DocumentTitle from 'react-document-title';
import PropTypes from 'prop-types';
import {withRouter} from 'react-router-dom';
import {connect} from 'react-redux';
import {bindActionCreators} from 'redux';
import isEmpty from 'lodash/isEmpty';
import isEqualWith from 'lodash/isEqualWith';
import {
  Header, Footer, Sidebar, PageLoader, ThemeContext, ErrorPage,
} from '..';
import ErrorHandler from '../Errors';
import {setSidebarStatus, setHelpStatus, setMediaSettings, updateUserCoins} from '../../../actions/user';
import {logOut} from '../../../actions/auth';
import {fetchTemplate} from '../../../actions/application';
import {fetchPaymentPacks} from '../../../actions/paimentPacks';
import {appId} from '../../../store';
import AblyService from '../../../utils/ably';
import {gameConfigSelector} from '../../../selectors';

export class Layout extends PureComponent {
  static propTypes = {
    auth: PropTypes.shape({
      isAuthenticated: PropTypes.bool.isRequired,
    }).isRequired,
    location: PropTypes.shape({
      pathname: PropTypes.string,
    }).isRequired,
    onLogout: PropTypes.func.isRequired,
    gameConfig: PropTypes.shape({}).isRequired,
    children: PropTypes.oneOfType([
      PropTypes.element,
      PropTypes.array,
    ]).isRequired,
    template: PropTypes.shape({}).isRequired,
    isInitialFetching: PropTypes.bool.isRequired,
    setTemplate: PropTypes.func,
    setPaymentPacks: PropTypes.func,
    router: PropTypes.shape({
      location: PropTypes.shape({
        pathname: PropTypes.string.isRequired,
      }).isRequired,
    }).isRequired,
    user: PropTypes.shape().isRequired,
    application: PropTypes.shape(),
    error: PropTypes.shape().isRequired,
    isRedemption: PropTypes.bool.isRequired,
    isSidebarVisible: PropTypes.bool,
    paymentPacks: PropTypes.shape(),
  };

  static defaultProps = {
    application: {},
    setTemplate: () => {},
    setPaymentPacks: () => {},
    isSidebarVisible: false,
    paymentPacks: {},
  };

  componentDidMount() {
    const {
      error: {error},
      isRedemption,
      setTemplate,
    } = this.props;

    if (!error && !isRedemption) {
      setTemplate(appId);
    }

    this.updatePresence();
  }

  componentDidUpdate(prevProps) {
    this.updatePresence(prevProps);
  }

  componentWillUnmount() {
    if (this.channel) {
      // ably v2: presence.leave() returns a Promise; fire-and-forget with a
      // catch so a rejected leave never surfaces as an unhandled rejection.
      this.channel.presence.leave().catch(err => console.error('Presence leave error:', err));
    }
  }

  compareUsers = (next, prev) => (
    ['id', 'firstName', 'lastName', 'image'].every(prop => next[prop] === prev[prop])
  );

  updatePresence(prevProps = {}) {
    const {
      auth: {isAuthenticated},
      isRedemption,
      user,
      setPaymentPacks,
    } = this.props;
    const {auth: prevAuth = {}, user: prevUser} = prevProps;
    const userLoaded = !isEmpty(user);
    const userChanged = userLoaded && !isEmpty(prevUser)
      && !isEqualWith(user, prevUser, this.compareUsers);

    if (isAuthenticated && !prevAuth.isAuthenticated) {
      setPaymentPacks(appId);
    }

    if (isRedemption) {
      return;
    }

    // Ensure the realtime client exists before using presence. Previously this
    // bailed whenever Ably had not authorized yet, and presence was entered only
    // on the one-shot prevUser->user transition. With persisted auth the user is
    // already loaded when Layout mounts but Ably authorizes later (when the
    // lobby/game route mounts), so that transition fired with no client, was
    // consumed, and never retried — the user never entered the `app/<id>` presence
    // set and so never appeared in the lobby's "People Online". RealTimeHOC also
    // calls authorize(); creating the client here is idempotent (both guard on
    // AblyService.realtime).
    if (!AblyService.realtime) {
      if (isAuthenticated && userLoaded) {
        AblyService.authorize();
      } else {
        return;
      }
    }

    if (!this.channel) {
      this.channel = AblyService.realtime.channels.get(`app/${appId}`);
    }

    const {
      id, firstName, lastName, image,
    } = user || {};

    if (userLoaded && !this.presenceEntered) {
      // Enter as soon as the client exists and the user is loaded, tracked with a
      // flag (not the one-shot transition) so it is retried on later renders.
      // presence.enter queues until the connection is established.
      this.presenceEntered = true;
      this.channel.presence.enter({
        id, firstName, lastName, image,
      })
        .catch((err) => {
          this.presenceEntered = false;
          console.error('Presence enter error:', err);
        });
    } else if (!userLoaded && this.presenceEntered) {
      this.presenceEntered = false;
      this.channel.presence.leave()
        .catch(err => console.error('Presence leave error:', err));
    } else if (userChanged) {
      this.channel.presence.update({
        id, firstName, lastName, image,
      })
        .catch(err => console.error('Presence update error:', err));
    }
  }

  _renderBody() {
    const {
      children,
      template,
      application,
      auth: {
        isAuthenticated,
      },
      paymentPacks,
      isSidebarVisible,
    } = this.props;
    const {isPaymentsPacksFetching, data: paymentPacksData = []} = paymentPacks || {};
    const {backgroundColor} = template;
    const authCls = isAuthenticated ? 'authenticated' : 'logout';
    let mainCls;

    if (!isAuthenticated) {
      mainCls = 'public-page';
    }

    return (
      <DocumentTitle title={application.name || 'Yak Network'}>
        <ThemeContext.Provider
          value={{
            ...template,
            paymentPacks: isPaymentsPacksFetching ? [] : paymentPacksData,
          }}
        >
          <div style={{backgroundColor}} className={authCls}>
            <Header {...this.props}/>
            <main className={mainCls} id="main">
              {children}
            </main>
            <Footer {...this.props}/>
            {isSidebarVisible && <Sidebar {...this.props}/>}
          </div>
          <ErrorHandler/>
        </ThemeContext.Provider>
      </DocumentTitle>
    );
  }

  _renderLoader = () => {
    const {template} = this.props;
    return (<PageLoader color={template.primaryColor} fullScreen/>);
  };

  _renderErrorBody = error => (
    <div>
      <Header {...this.props}/>
      <main>
        <ErrorPage message={error}/>
      </main>
      <Footer {...this.props}/>
    </div>
  );

  render() {
    const {isInitialFetching, error: {error}} = this.props;

    if (isInitialFetching && error) {
      return this._renderErrorBody(error);
    }

    return isInitialFetching ? this._renderLoader() : this._renderBody();
  }
}

const mapStateToProps = (state) => {
  const {
    auth,
    paymentPacks,
    router,
    application: {
      template = {}, isTemplateFetching = false, isRedemption, details,
    },
    user: {profile, sidebar, coins},
    error,
  } = state;

  return {
    auth,
    application: details,
    coins,
    paymentPacks,
    router,
    template,
    isInitialFetching: isTemplateFetching,
    gameConfig: gameConfigSelector(state),
    user: profile,
    isSidebarVisible: sidebar,
    error,
    isRedemption,
  };
};
const mapDispatchToProps = dispatch => bindActionCreators(
  {
    helpToggle: setHelpStatus,
    sidebarToggle: setSidebarStatus,
    onLogout: logOut,
    setTemplate: fetchTemplate,
    setPaymentPacks: fetchPaymentPacks,
    setMediaSettings,
    updateUserCoins,
  },
  dispatch
);

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(Layout));
